import React, { useState } from "react";
import classNames from "classnames";
import { toast } from "react-toastify";
import { useLayoutContext } from "../../core/context/layout-context";
import {
  EXPEDITION_RECHERCHE,
  EXPEDITION_TRACKING,
  EXPEDITION_INTERNATIONAL,
  EXPEDITION_ENVOIS,
  EXPEDITION_URBAIN,
  EXPEDITION_COMMERCE,
  EXPEDITION_MARCHAND,
  EXPEDITION_TICKET,
  EXPEDITION_VOYAGE,
  EXPEDITION_INCIDENT,
  EXPEDITION_RECLAMATION,
  EXPEDITION_REMBOURSEMENT,
} from "../../core/constants/layout";
import TrackingColis from "./components/tracking-colis";
import EnvoisPage from "./components/envois-console";
import UrbainPage from "./components/urbain-console";
import { UrbainWizardModal } from "./components/urbain-modals";
import { UrbanItem } from "./components/urbain-shared";
import ReclamationsConsole from "../../components/reclamations/reclamations-console";
import { EXPEDITION_RECLAM_CONFIG } from "./components/reclamations-config";
import CommerceConsole from "./components/commerce-console";
import MarchandConsole from "./components/marchand-console";
import {
  FloatingActionsRail,
  IntlWizardModal,
  ProspectModal,
  SuiviColisModal,
} from "./components/expedition-modals";

export default function ExpeditionsPage() {
  const { selectChildren } = useLayoutContext();

  // Urban deliveries live here so the floating "Urbain" action can add to the list from any sub-page.
  const [urbanItems, setUrbanItems] = useState<UrbanItem[]>([]);

  // Floating Actions Modals
  const [wizardOpen, setWizardOpen] = useState(false);
  const [urbainOpen, setUrbainOpen] = useState(false);
  const [prospectOpen, setProspectOpen] = useState(false);
  const [suiviModalOpen, setSuiviModalOpen] = useState(false);

  const flash = (msg: string) => {
    toast.info(msg, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: true,
      theme: "colored",
    });
  };

  const renderContent = () => {
    switch (selectChildren) {
      case EXPEDITION_TRACKING:
        return <TrackingColis />;
      case EXPEDITION_INTERNATIONAL:
      case EXPEDITION_ENVOIS:
      case "#international":
      case "#envois":
      case EXPEDITION_TICKET:
        return <EnvoisPage onOpenIntlWizard={() => setWizardOpen(true)} />;
      case EXPEDITION_URBAIN:
        return <UrbainPage data={urbanItems} setData={setUrbanItems} onOpenWizard={() => setUrbainOpen(true)} />;
      case EXPEDITION_COMMERCE:
      case "#commerce":
        return <CommerceConsole />;
      case EXPEDITION_MARCHAND:
      case "#marchand":
        return <MarchandConsole />;
      case EXPEDITION_RECHERCHE:
        return (
          <div className="p-8">
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-xs">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Moteur de recherche Expéditions
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Recherchez des expéditions par itinéraire, date ou agence.
              </p>
              <div className="rounded-lg border-2 border-dashed border-gray-200 p-10 text-center text-gray-400">
                Interface de recherche avancée
              </div>
            </div>
          </div>
        );
      case EXPEDITION_VOYAGE:
        return (
          <div className="p-8">
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-xs">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Voyages & Transits</h2>
              <p className="text-sm text-gray-500">Suivi des trajets aériens, maritimes et urbains.</p>
            </div>
          </div>
        );
      case EXPEDITION_INCIDENT:
        return (
          <div className="p-8">
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-xs">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Incidents & Retards</h2>
              <p className="text-sm text-gray-500">Signalements et gestion des litiges transport.</p>
            </div>
          </div>
        );
      case EXPEDITION_RECLAMATION:
        return <ReclamationsConsole config={EXPEDITION_RECLAM_CONFIG} />;
      case EXPEDITION_REMBOURSEMENT:
        return (
          <div className="p-8">
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-xs">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Remboursements</h2>
              <p className="text-sm text-gray-500">Historique et validation des remboursements fret.</p>
            </div>
          </div>
        );
      default:
        return <TrackingColis />;
    }
  };

  return (
    <div className={classNames("pt-[50px] pb-6 min-h-screen bg-[#faf7ff] flex flex-col relative")}>
      <div className="flex-1 min-h-0 flex flex-col">
        {renderContent()}
      </div>

      {/* Floating Action Rail accessible across all expedition sub-pages */}
      <FloatingActionsRail
        onOpenIntlWizard={() => setWizardOpen(true)}
        onOpenUrbainModal={() => setUrbainOpen(true)}
        onOpenProspectModal={() => setProspectOpen(true)}
        onOpenManifest={() => flash("Manifeste du prochain départ affiché")}
        onOpenSuiviColisModal={() => setSuiviModalOpen(true)}
        onFlash={flash}
      />

      {/* International Stepper Wizard (Exact user HTML replica) */}
      <IntlWizardModal
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={(code) => flash(`Nouvelle expédition créée : ${code}`)}
        onFlash={flash}
      />

      {/* Quick Action: Livraison Urbaine */}
      <UrbainWizardModal
        isOpen={urbainOpen}
        onClose={() => setUrbainOpen(false)}
        onSuccess={(item) => {
          setUrbanItems((prev) => [item, ...prev]);
          flash(`Livraison urbaine ${item.code} créée avec succès`);
        }}
        onFlash={flash}
      />

      {/* Quick Action: Prospect / Devis */}
      <ProspectModal
        isOpen={prospectOpen}
        onClose={() => setProspectOpen(false)}
        onSuccess={(name) => flash(`Prospect ${name} enregistré avec succès`)}
        onFlash={flash}
      />

      {/* Quick Action: Direct Tracking Modal (Without page redirect) */}
      <SuiviColisModal
        isOpen={suiviModalOpen}
        onClose={() => setSuiviModalOpen(false)}
        onFlash={flash}
      />
    </div>
  );
}
